import { ReactNode, MouseEvent } from 'react';
import { ChevronDown, X } from 'lucide-react';

export type ChipVariant = 'default' | 'dashed' | 'active';

export interface ChipProps {
  label?: ReactNode;
  value?: ReactNode;
  variant?: ChipVariant;
  onClick?: () => void;
  onClear?: () => void;
  /**
   * Optional aria-label for the clear-X button. Falls back to
   * `Clear ${label}` when `label` is a string, else "Clear filter".
   */
  clearLabel?: string;
  chevron?: boolean;
  children?: ReactNode;
  className?: string;
  title?: string;
}

export function Chip({
  label,
  value,
  variant = 'default',
  onClick,
  onClear,
  clearLabel,
  chevron = true,
  children,
  className = '',
  title,
}: ChipProps) {
  const classes = [
    'ds-chip',
    variant === 'dashed' ? 'ds-chip-dashed' : '',
    variant === 'active' ? 'ds-chip-active' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const handleClear = (e: MouseEvent) => {
    if (!onClear) return;
    e.stopPropagation();
    onClear();
  };

  return (
    <button type="button" className={classes} onClick={onClick} title={title}>
      {children ? (
        children
      ) : (
        <>
          {label != null && <span className="ds-chip-label">{label}</span>}
          {value != null && <span className="ds-chip-value">{value}</span>}
          {onClear ? (
            <span
              className="ds-chip-x"
              onClick={handleClear}
              aria-label={
                clearLabel ||
                (typeof label === 'string' && label ? `Clear ${label}` : 'Clear filter')
              }
            >
              <X size={12} />
            </span>
          ) : chevron && variant !== 'dashed' ? (
            <ChevronDown size={12} className="ds-chip-chev" />
          ) : null}
        </>
      )}
    </button>
  );
}

export default Chip;
